/**
 * MONSTER TOWN — VENUE DATA MODEL. OWNED BY THE VENUE CREW (src/venue/**).
 *
 * THE WHOLE POINT OF THIS FILE: venue #2 (zoo / garden / park / pet shop) must
 * be a DATA DROP, not an engine change. Everything the service loop needs to
 * know about a venue lives in a `VenueDef` — the room, the menu, which
 * placeables are stations, which are seats, and what the starter kit is. The
 * simulation in `service.ts` reads only these fields; it has no idea what ice
 * cream is.
 *
 * To add venue #2 you write ONE `defineVenue({...})` in `src/content/venue.ts`
 * plus its placeables. No file in this directory changes.
 */

import type { Cell, GridSpec, Rot } from '../core/grid'
import type { PaletteName } from '../core/palette'
import type { PlaceableId, SceneId, VenueId, VenueKind } from '../core/types'

/**
 * The three-step sequence a kid assembles, in order. Named generically so a
 * zoo can reuse them (base = bowl, scoop = food, topping = treat).
 */
export type VenueItemKind = 'base' | 'scoop' | 'topping'

/**
 * How an item is drawn, both in the world and on an order card.
 *
 * THIS IS THE ONLY THING THAT DECIDES A PICTURE. It used to be possible for a
 * card to guess from the item's *id* string (`includes('cone')`), which is why
 * the restaurant's `food_pasta` fell through to a pink ice-cream scoop and the
 * restaurant served ice cream on its tickets. Shapes are declared, never
 * inferred — a venue whose menu is animals or plants just adds a shape here.
 */
export type VenueItemShape =
  | 'cone'
  | 'cup'
  | 'plate'
  | 'ball'
  | 'cherry'
  | 'sprinkles'
  | 'sauce'

export interface VenueItem {
  readonly id: string
  readonly kind: VenueItemKind
  readonly color: PaletteName
  readonly shape: VenueItemShape
  /** The placeable that dispenses it. If it is not built, it is never ordered. */
  readonly stationDefId: PlaceableId
}

/**
 * How the floor of a room is patterned. A venue that is only a COLOUR SWAP of
 * another venue does not read as somewhere new — the restaurant and the ice
 * cream shop were the same checkerboard in tan instead of mint, which is half
 * of why a kid would not have believed they had walked into a different place.
 * The pattern is shell DATA, so venue #3 picks one without touching the room
 * builder.
 */
export type VenueFloorStyle = 'checker' | 'planks' | 'diamond'

export interface VenueShell {
  readonly wall: PaletteName
  readonly wallTrim: PaletteName
  readonly floor: PaletteName
  readonly floorAlt: PaletteName
  readonly roofBeam: PaletteName
  /** Defaults to `checker` so existing shells are unchanged. */
  readonly floorStyle?: VenueFloorStyle
  /** Centre rug colour. `null` means no rug at all. Defaults to `floorRug`. */
  readonly rug?: PaletteName | null
}

export interface VenueDef {
  readonly id: VenueId
  readonly kind: VenueKind
  readonly sceneId: SceneId
  /** Dev-facing only. Never shown to a kid (LAW #4). */
  readonly devName: string
  readonly grid: GridSpec
  readonly shell: VenueShell
  /**
   * Cell inside the venue you stand on to leave. Also where customers walk in
   * from. The world crew's building declares `toScene: <this venue's sceneId>`,
   * so its front door is the town-side half of the same doorway.
   */
  readonly doorCell: Cell
  /** Everything on the menu. Availability is derived from what is BUILT. */
  readonly items: readonly VenueItem[]
  /** Placeables that count as somewhere a customer can sit. */
  readonly seatDefIds: readonly PlaceableId[]
  /** Never ask for more than this many scoops, ever. */
  readonly maxScoops: number
  /** The kit the venue opens with, so it is playable the second it exists. */
  readonly starter: readonly { defId: PlaceableId; anchor: Cell; rot?: Rot }[]
}

// ---------------------------------------------------------------------------
// orders & trays
// ---------------------------------------------------------------------------

/** What a customer asked for, in picture-speech. */
export interface VenueOrder {
  readonly base: string
  readonly scoops: readonly string[]
  readonly topping: string | null
}

/** What the kid is currently carrying. Never rushed, never lost (LAW #2). */
export interface VenueTray {
  base: string | null
  scoops: string[]
  topping: string | null
}

export function emptyTray(): VenueTray {
  return { base: null, scoops: [], topping: null }
}

export function trayIsEmpty(t: VenueTray): boolean {
  return t.base === null && t.scoops.length === 0 && t.topping === null
}

// ---------------------------------------------------------------------------
// PICTURES — what an order LOOKS like, resolved from data, never from an id
// ---------------------------------------------------------------------------

/** One drawable thing: its declared shape and its declared colour. */
export interface VenuePic {
  readonly shape: VenueItemShape
  readonly color: PaletteName
}

/**
 * A whole serving as pictures, in the three slots a card always shows.
 *
 * The HUD renders exactly this and nothing else. It cannot look at an item id,
 * so it cannot guess wrong, and a new venue's menu is drawn correctly on the
 * day it is written with no HUD change at all.
 */
export interface VenuePics {
  readonly base: VenuePic | null
  readonly scoops: readonly VenuePic[]
  readonly topping: VenuePic | null
}

export const EMPTY_PICS: VenuePics = { base: null, scoops: [], topping: null }

function picOf(items: readonly VenueItem[], id: string | null): VenuePic | null {
  if (id === null) return null
  const it = items.find((i) => i.id === id)
  return it === undefined ? null : { shape: it.shape, color: it.color }
}

/** Resolve a serving (an order OR a tray) into pictures. */
export function picturesFor(
  items: readonly VenueItem[],
  parts: { base: string | null; scoops: readonly string[]; topping: string | null },
): VenuePics {
  const scoops: VenuePic[] = []
  for (const s of parts.scoops) {
    const p = picOf(items, s)
    if (p !== null) scoops.push(p)
  }
  return { base: picOf(items, parts.base), scoops, topping: picOf(items, parts.topping) }
}

/**
 * FORGIVING MATCH. A wrong order is a giggle and a retry, never a fail, so the
 * rules lean generous:
 *  - the base must match (cone vs cup — it is the thing a kid sees first),
 *  - the scoops must be the same multiset (this is the counting lesson),
 *  - a topping that was ASKED FOR must be there; an extra topping nobody asked
 *    for is a gift, not a mistake.
 */
export function trayMatches(tray: VenueTray, order: VenueOrder): boolean {
  if (tray.base !== order.base) return false
  if (tray.scoops.length !== order.scoops.length) return false
  const want = [...order.scoops].sort()
  const have = [...tray.scoops].sort()
  for (let i = 0; i < want.length; i++) {
    if (want[i] !== have[i]) return false
  }
  if (order.topping !== null && tray.topping !== order.topping) return false
  return true
}

// ---------------------------------------------------------------------------
// registry
// ---------------------------------------------------------------------------

const VENUES = new Map<VenueId, VenueDef>()

export function defineVenue(def: VenueDef): VenueDef {
  return def
}

export function registerVenue(def: VenueDef): void {
  if (VENUES.has(def.id)) throw new Error(`venue "${def.id}" registered twice`)
  VENUES.set(def.id, def)
}

export function registerVenues(defs: readonly VenueDef[]): void {
  for (const d of defs) registerVenue(d)
}

export function allVenues(): VenueDef[] {
  return [...VENUES.values()]
}

export function venue(id: VenueId): VenueDef | undefined {
  return VENUES.get(id)
}

export function venueForScene(sceneId: SceneId): VenueDef | undefined {
  for (const v of VENUES.values()) if (v.sceneId === sceneId) return v
  return undefined
}

export function venueItem(def: VenueDef, itemId: string): VenueItem | undefined {
  return def.items.find((i) => i.id === itemId)
}
